/**
 * Offline Queue — IndexedDB-backed mutation queue with auto-retry.
 *
 * Usage:
 *   const result = await queuedFetch<T>('/api/shipments/123', { method: 'PUT', body: ... });
 *   if (result.queued) // saved offline, will sync later
 *   else              // result.data is the API response
 */
import { get, set, del, keys } from 'idb-keyval';

// ── Types ──

export interface QueuedMutation {
    id: string;
    timestamp: number;
    endpoint: string;
    method: string;
    body: string | null;
    headers: Record<string, string>;
    retryCount: number;
    status: 'pending' | 'syncing' | 'failed';
    /** Human-readable label for UI */
    label: string;
}

export type QueuedFetchResult<T> =
    | { queued: false; data: T }
    | { queued: true; mutationId: string };

export type SyncStatus = 'idle' | 'syncing' | 'has-pending' | 'has-failed';

// ── Constants ──

const KEY_PREFIX = 'oq:';
const MAX_RETRIES = 5;

// ── Listeners ──

type SyncListener = (status: SyncStatus, count: number) => void;
const _listeners = new Set<SyncListener>();

export function subscribeSyncStatus(fn: SyncListener): () => void {
    _listeners.add(fn);
    // Immediately notify with current state
    _getStatusAndCount().then(([s, c]) => fn(s, c));
    return () => _listeners.delete(fn);
}

function _notify(status: SyncStatus, count: number) {
    _listeners.forEach((fn) => fn(status, count));
}

async function _getStatusAndCount(): Promise<[SyncStatus, number]> {
    const all = await getAllMutations();
    if (all.length === 0) return ['idle', 0];
    if (all.some((m) => m.status === 'syncing')) return ['syncing', all.length];
    if (all.some((m) => m.status === 'failed')) return ['has-failed', all.length];
    return ['has-pending', all.length];
}

// ── Queue Operations ──

function _key(id: string) {
    return `${KEY_PREFIX}${id}`;
}

export async function enqueue(mutation: Omit<QueuedMutation, 'id' | 'timestamp' | 'retryCount' | 'status'>): Promise<string> {
    const id = `mut_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const entry: QueuedMutation = {
        ...mutation,
        id,
        timestamp: Date.now(),
        retryCount: 0,
        status: 'pending',
    };
    await set(_key(id), entry);
    const [s, c] = await _getStatusAndCount();
    _notify(s, c);
    console.log(`[OfflineQueue] Enqueued: ${mutation.method} ${mutation.endpoint} (${id})`);
    return id;
}

export async function dequeue(id: string): Promise<void> {
    await del(_key(id));
    const [s, c] = await _getStatusAndCount();
    _notify(s, c);
}

export async function getAllMutations(): Promise<QueuedMutation[]> {
    const allKeys = await keys();
    const mutKeys = allKeys.filter((k) => String(k).startsWith(KEY_PREFIX));
    const entries = await Promise.all(mutKeys.map((k) => get<QueuedMutation>(k)));
    return entries
        .filter((e): e is QueuedMutation => !!e)
        .sort((a, b) => a.timestamp - b.timestamp); // FIFO
}

export async function getPendingCount(): Promise<number> {
    const all = await getAllMutations();
    return all.filter((m) => m.status !== 'failed').length;
}

export async function clearFailed(): Promise<void> {
    const all = await getAllMutations();
    await Promise.all(all.filter((m) => m.status === 'failed').map((m) => del(_key(m.id))));
    const [s, c] = await _getStatusAndCount();
    _notify(s, c);
}

export async function clearAll(): Promise<number> {
    const all = await getAllMutations();
    await Promise.all(all.map((m) => del(_key(m.id))));
    _notify('idle', 0);
    console.log(`[OfflineQueue] Cleared all ${all.length} mutations`);
    return all.length;
}

// ── Flush (sync pending mutations) ──

let _flushing = false;

export async function flush(): Promise<{ synced: number; failed: number }> {
    if (_flushing) return { synced: 0, failed: 0 };
    _flushing = true;
    _notify('syncing', 0);

    const mutations = await getAllMutations();
    const pending = mutations.filter((m) => m.status !== 'failed');
    let synced = 0;
    let failed = 0;

    for (const mut of pending) {
        // Mark as syncing
        await set(_key(mut.id), { ...mut, status: 'syncing' as const });
        _notify('syncing', pending.length - synced);

        try {
            const res = await fetch(mut.endpoint, {
                method: mut.method,
                headers: mut.headers,
                body: mut.body,
            });

            if (res.ok) {
                await dequeue(mut.id);
                synced++;
                console.log(`[OfflineQueue] Synced: ${mut.method} ${mut.endpoint}`);
            } else if (res.status === 404) {
                // Resource doesn't exist — dequeue, never retry
                await dequeue(mut.id);
                failed++;
                console.warn(`[OfflineQueue] 404 — removed stale mutation: ${mut.endpoint}`);
            } else if (res.status === 409) {
                // Conflict — mark as failed, don't retry
                await set(_key(mut.id), { ...mut, status: 'failed' as const });
                failed++;
                console.warn(`[OfflineQueue] Conflict (409): ${mut.endpoint} — требуется ручная сверка`);
            } else {
                throw new Error(`HTTP ${res.status}`);
            }
        } catch (err) {
            const newCount = mut.retryCount + 1;
            if (newCount >= MAX_RETRIES) {
                await set(_key(mut.id), { ...mut, retryCount: newCount, status: 'failed' as const });
                failed++;
                console.error(`[OfflineQueue] Failed after ${MAX_RETRIES} retries: ${mut.endpoint}`);
            } else {
                await set(_key(mut.id), { ...mut, retryCount: newCount, status: 'pending' as const });
                console.warn(`[OfflineQueue] Retry ${newCount}/${MAX_RETRIES}: ${mut.endpoint}`, err);
            }
        }
    }

    _flushing = false;
    const [s, c] = await _getStatusAndCount();
    _notify(s, c);
    return { synced, failed };
}

// ── Auto-flush on reconnect ──

let _initialized = false;

export function initOfflineQueue(): void {
    if (_initialized) return;
    _initialized = true;


    window.addEventListener('online', async () => {
        console.log('[OfflineQueue] Network restored — flushing queue');
        const result = await flush();
        if (result.synced > 0) {
            // Dispatch custom event for toast notification
            window.dispatchEvent(
                new CustomEvent('offline-queue-synced', { detail: result }),
            );
        }
    });

    // Initial flush (in case there are pending mutations from a previous session)
    if (navigator.onLine) {
        flush();
    }

    console.log('[OfflineQueue] Initialized');
}

// ── queuedFetch — main API for components ──

/**
 * Wrapper around fetch that auto-queues on failure or offline.
 * 
 * @param fullUrl   Full URL (e.g. http://localhost:5000/api/shipments/123)
 * @param options   Standard RequestInit
 * @param label     Human-readable label for the sync indicator
 */
export async function queuedFetch<T>(
    fullUrl: string,
    options: RequestInit & { method: string },
    label: string,
): Promise<QueuedFetchResult<T>> {
    const headers: Record<string, string> = {};
    if (options.headers) {
        if (options.headers instanceof Headers) {
            options.headers.forEach((v, k) => { headers[k] = v; });
        } else if (Array.isArray(options.headers)) {
            options.headers.forEach(([k, v]) => { headers[k] = v; });
        } else {
            Object.assign(headers, options.headers);
        }
    }

    // If offline — queue immediately
    if (!navigator.onLine) {
        const id = await enqueue({
            endpoint: fullUrl,
            method: options.method,
            body: typeof options.body === 'string' ? options.body : null,
            headers,
            label,
        });
        return { queued: true, mutationId: id };
    }

    // Online — try the real request
    try {
        const res = await fetch(fullUrl, options);
        if (!res.ok) {
            const detail = await res.text().catch(() => res.statusText);
            throw new Error(`API ${res.status}: ${detail}`);
        }
        const data: T = await res.json();
        return { queued: false, data };
    } catch (err) {
        // Network error or server error — queue for retry
        console.warn('[OfflineQueue] Request failed, queueing:', err);
        const id = await enqueue({
            endpoint: fullUrl,
            method: options.method,
            body: typeof options.body === 'string' ? options.body : null,
            headers,
            label,
        });
        return { queued: true, mutationId: id };
    }
}
